import { NavigationMenu, NavigationMenuItem } from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Button } from "../ui/button";
import { MarketingLink } from "@/components/OptionalLink";
import { useRouter } from "next/router";
import { useSession } from "@/components/useSession";
import { capitalize, cn, fetcherIgnore404 } from "@/lib/utils";
import { User } from "@/types";
import { JSONSafe } from "@/types";
import useSWR from "swr";
import logger from "@/lib/logger";
import { AlignJustify, ChevronDown } from "lucide-react";
import React from "react";
import { ThemeToggle } from "@/components/ThemeToggle";

interface SubRouteProps {
  id: string;
  href: string;
  label: string;
  description?: string;
}

interface RouteProps {
  id: string;
  href: string;
  label: string;
  active: boolean;
  primary?: boolean;
  secondary?: boolean;
  overflow?: boolean | "only";
  className?: string;
  children?: SubRouteProps[];
}

const getMenu = (path: string, signedIn: boolean): RouteProps[] => [
  {
    id: "product",
    href: "/product/about",
    label: "Product",
    active: path.startsWith("/product"),
    overflow: true,
    children: [
      { id: "product-about", href: "/product/about", label: "How It Works" },
      { id: "product-features", href: "/product/features", label: "Features" },
      { id: "product-testimonials", href: "/product/testimonials", label: "Testimonials" },
      { id: "product-comparison", href: "/product/comparison", label: "Comparison" }
    ]
  },
  {
    id: "pricing",
    href: "/pricing",
    label: "Pricing",
    active: path === "/pricing",
    overflow: true
  },
  {
    id: "templates",
    href: "/r",
    label: "Templates",
    active: false,
    overflow: true,
    children: [
      {
        id: "boilerplate",
        href: "/boilerplate",
        label: "Kickstart your project"
      },
      {
        id: "templates-popular",
        href: "/popular",
        label: "Popular templates"
      },
      {
        id: "templates",
        href: "/r",
        label: "See all templates"
      }
    ]
  },
  {
    id: "resources",
    href: "/resources",
    label: "Resources",
    active: false,
    overflow: true,
    children: [
      {
        id: "blog",
        href: "/blog",
        label: "Blog"
      },
      {
        id: "docs",
        href: "https://docs.dollardeploy.com",
        label: "Documentation"
      },
      {
        id: "faq",
        href: "/faq",
        label: "FAQ"
      },
      {
        id: "team",
        href: "/team",
        label: "About us"
      }
    ]
  },
  {
    id: "docs",
    href: "https://docs.dollardeploy.com",
    label: "Documentation",
    active: path === "/docs",
    overflow: "only"
  },
  ...(signedIn
    ? [
        {
          id: "dashboard",
          href: "/dashboard",
          label: "Dashboard",
          active: path === "/dashboard",
          primary: true,
          className: "md:min-w-[100px]"
        }
      ]
    : [
        {
          id: "signUp",
          href: "/auth/signUp?callbackUrl=/dashboard",
          label: "Sign Up",
          active: path === "/auth/signUp",
          primary: true,
          className: "md:min-w-[100px]"
        },
        {
          id: "signIn",
          href: "/auth/signIn?callbackUrl=/dashboard",
          label: "Sign In",
          active: path === "/auth/signIn",
          secondary: true,
          className: "md:min-w-[100px]"
        }
      ])
];

/** Icon only */
export const Icon = ({
  dark = false,
  light = false,
  className = "",
  counter
}: {
  dark?: boolean;
  light?: boolean;
  className?: string;
  counter?: number;
}) => (
  <div className="flex flex-row items-center justify-center relative">
    <img
      src="/logo.svg"
      className={cn("h-5", dark ? "" : light ? "invert-100" : "dark:invert-100", className)}
      alt="DollarDeploy"
    />
    {counter && (
      <span className="absolute bottom-1 right-1 text-[10px] bg-primary border border-secondary text-primary-foreground rounded-full px-1">
        {counter}
      </span>
    )}
  </div>
);

/** Icon + Brand name */
export const Logo = ({ className }: { className?: string }) => (
  <div>
    <img
      src="/logo-main.svg"
      className={cn("h-6 dark:invert-100", className)}
      alt="DollarDeploy"
    />
  </div>
);

export const applyFlags = (user?: Pick<User, "flags">) => {
  if (!user) {
    return;
  }

  if (user.flags.includes("tracking-")) {
    if (!localStorage.getItem("plausible_ignore")) {
      logger.info("Disabling tracking");
      localStorage.setItem("plausible_ignore", "true");
    }
  } else if (localStorage.getItem("plausible_ignore")) {
    logger.info("Enabling tracking");
    localStorage.removeItem("plausible_ignore");
  }
};

const navLinkClass =
  "ml-3 font-semibold text-sm hover:text-primary flex items-center gap-1 transition-colors";

const DesktopNavItem = ({ route }: { route: RouteProps }) => {
  const [open, setOpen] = React.useState(false);

  if (!route.children || route.children.length === 0) {
    return (
      <MarketingLink
        rel="noreferrer noopener"
        href={route.href}
        className={cn(navLinkClass, `plausible-event-name=${capitalize(route.id)}`)}
      >
        {route.label}
      </MarketingLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={() => setOpen(false)}
      onFocus={() => setOpen(true)}
    >
      <button
        aria-expanded={open}
        aria-haspopup="true"
        className={cn(
          navLinkClass,
          open && "text-primary",
          `plausible-event-name=${capitalize(route.id)}`
        )}
      >
        {route.label}
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      {/* Absolutely positioned — still a DOM child, so onMouseLeave above won't fire in the gap */}
      <div
        className={cn(
          "absolute top-full left-0 z-50 min-w-[180px] rounded-md border border-border/40",
          "bg-background/80 backdrop-blur-md shadow-lg p-1",
          "transition-all duration-150 ease-out",
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-1 pointer-events-none"
        )}
      >
        {route.children.map(child => (
          <MarketingLink
            key={child.id}
            href={child.href}
            onClick={() => setOpen(false)}
            className={cn(
              "flex flex-col gap-0.5 items-start rounded-sm px-3 py-2 w-full",
              "text-sm hover:bg-accent hover:text-accent-foreground transition-colors",
              `plausible-event-name=${capitalize(child.id)}`
            )}
          >
            <span className="font-medium">{child.label}</span>
            {child.description && (
              <span className="text-xs text-muted-foreground font-normal">
                {child.description}
              </span>
            )}
          </MarketingLink>
        ))}
      </div>
    </div>
  );
};

const MobileNavItem = ({ route, onClose }: { route: RouteProps; onClose: () => void }) => {
  const [open, setOpen] = React.useState(false);

  if (!route.children || route.children.length === 0) {
    return (
      <Button
        asChild
        onClick={onClose}
        variant={route.primary ? "default" : "ghost"}
        className={cn("justify-start", `plausible-event-name=${capitalize(route.id)}`)}
      >
        <MarketingLink href={route.href}>{route.label}</MarketingLink>
      </Button>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-between ml-1",
            `plausible-event-name=${capitalize(route.id)}`
          )}
        >
          {route.label}
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-1 pl-3 pt-1 border-l border-border ml-3">
        {route.children.map(child => (
          <MarketingLink
            key={child.id}
            href={child.href}
            onClick={onClose}
            className={cn(
              "flex flex-col items-start gap-0.5 rounded-md px-3 py-2 text-sm",
              "hover:bg-accent hover:text-accent-foreground transition-colors",
              `plausible-event-name=${capitalize(child.id)}`
            )}
          >
            <span className="font-medium">{child.label}</span>
            {child.description && (
              <span className="text-xs text-muted-foreground font-normal">
                {child.description}
              </span>
            )}
          </MarketingLink>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const Navbar = () => {
  const router = useRouter();
  const { data: session } = useSession();
  const { data: user } = useSWR<JSONSafe<User>>("/api/user", fetcherIgnore404);
  applyFlags(user);
  const [isOpen, setIsOpen] = React.useState(false);

  const currentMenu = getMenu(router.pathname, !!session?.user);

  const desktopNavItems = currentMenu.filter(
    route => route.overflow !== "only" && !route.primary && !route.secondary
  );
  const ctaItems = currentMenu.filter(route => !route.overflow);
  const mobileItems = currentMenu.filter(route => !route.primary);

  return (
    <header className="absolute top-0 left-0 w-full">
      <NavigationMenu className="list-none container max-w-[60rem] flex flex-col md:flex-row gap-1.5 items-center justify-between py-3">
        <div className="flex items-center">
          <NavigationMenuItem>
            <MarketingLink
              rel="noreferrer noopener"
              href="/#stay"
              className="font-bold text-md md:text-xl plausible-event-name=Home"
            >
              <Logo className="h-6 mt-0.5" />
            </MarketingLink>
          </NavigationMenuItem>

          <div className="hidden md:flex items-center">
            {desktopNavItems.map(route => (
              <DesktopNavItem key={route.id} route={route} />
            ))}
          </div>
        </div>

        <div className="flex items-center">
          {ctaItems.map(route => (
            <Button
              asChild
              rel="noreferrer noopener"
              key={route.id}
              size="sm"
              variant={route.primary ? "default" : "outline"}
              className={cn(
                "mr-1 md:mr-1.5",
                route.className,
                `plausible-event-name=${capitalize(route.id)}`
              )}
            >
              <MarketingLink href={route.href}>{route.label}</MarketingLink>
            </Button>
          ))}

          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden ml-1">
              <Button variant="outline" size="icon" className="h-8 w-8">
                <AlignJustify className="h-6 w-6" />
                <span className="sr-only">Toggle Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[240px] sm:w-[400px] pl-2">
              <SheetHeader>
                <SheetTitle className="mt-[-3px]">
                  <Logo className="h-5 mt-0.5" />
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 mt-4">
                {mobileItems.map(route => (
                  <MobileNavItem
                    key={route.id}
                    route={route}
                    onClose={() => setIsOpen(false)}
                  />
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <ThemeToggle />
        </div>
      </NavigationMenu>
    </header>
  );
};
